import React from 'react';
import { useTranslate } from '@tolgee/react';

import { LomavisStatusCard1 } from '@/lomavisSharedUi/atoms/feedback/LomavisStatusCard1';

interface Props {
  statusKey: 'draft' | 'publishing' | 'published' | 'failed';
  publishedAt?: string | null;
  failureMessage?: string;
  metaCampaignId?: string;
}

const formatPublishedAt = (
  value: string | null | undefined,
  locale: string
) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return value;
  }
};

const metaCampaignUrl = (campaignId: string) =>
  `https://business.facebook.com/adsmanager/manage/campaigns?selected_campaign_ids=${encodeURIComponent(
    campaignId
  )}`;

export const AdManagerStatusBanner: React.FC<Props> = ({
  statusKey,
  publishedAt,
  failureMessage,
  metaCampaignId
}) => {
  const { t } = useTranslate();

  if (statusKey === 'draft') return null;

  const locale =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language
      : 'en-US';

  if (statusKey === 'publishing') {
    return (
      <LomavisStatusCard1
        className="mt-3 mb-4"
        variant="info"
        busy
        title={t('adManager.status.publishingTitle', 'Publishing to Meta…')}
        description={t(
          'adManager.status.publishingDescription',
          'Meta is reviewing this campaign. The status updates automatically when it changes.'
        )}
      />
    );
  }

  if (statusKey === 'published') {
    return (
      <LomavisStatusCard1
        className="mt-3 mb-4"
        variant="success"
        title={t('adManager.status.publishedTitle', 'Published on Meta')}
        description={
          publishedAt
            ? t(
                'adManager.status.publishedDescription',
                'Live since {when}. The campaign is running from Meta and cannot be edited from here.',
                { when: formatPublishedAt(publishedAt, locale) }
              )
            : t(
                'adManager.status.publishedDescriptionNoTime',
                'The campaign is running from Meta and cannot be edited from here.'
              )
        }
        action={
          metaCampaignId ? (
            <a
              className="btn btn-outline-success btn-sm"
              href={metaCampaignUrl(metaCampaignId)}
              target="_blank"
              rel="noreferrer noopener"
            >
              <i
                className="fa-solid fa-arrow-up-right-from-square me-2"
                aria-hidden="true"
              />
              {t('adManager.status.openInMeta', 'Open in Meta Ads Manager')}
            </a>
          ) : null
        }
      />
    );
  }

  return (
    <LomavisStatusCard1
      className="mt-3 mb-4"
      variant="danger"
      title={t('adManager.status.failedTitle', 'Publishing failed')}
      description={
        failureMessage?.trim() ||
        t(
          'adManager.status.failedDescription',
          'Meta rejected the last publish attempt. Fix the reported issue and try again.'
        )
      }
    />
  );
};

export default AdManagerStatusBanner;
